import React from "react";
import LazyImage from "app/components/LazyImage";
import LetterCard from "app/components/LetterCard";
import ReactIcon from "app/components/ReactIcon";
import { Skeleton } from "app/components/Skeleton";
import { useActivityItem } from "app/containers/Features/containers/Home/hooks/useActivityItem";
import { HighlightQuotedText } from "./HighlightQuotedText";
import { IAvatar } from "app/containers/Features/containers/TeamSettings/container/MySetting/types";
import { dateFormatter, getInitials } from "utils/helpers";
import { IProjectDetails, IUserActivity } from "../type";
import { IWorkspaceTeams } from "app/types";

interface ActivityItemContentProps {
  item: IUserActivity;
  selectedTeam:  IWorkspaceTeams;
  homeProjectDetails:  IProjectDetails[];
}

const renderLetter = (avatar: IAvatar | undefined, name: string) => {
  if (avatar?.file) {
    return (
      <LazyImage
        src={avatar.file}
        alt={name}
        className="size-[12px] object-cover"
      />
    );
  }
  if (avatar?.icon) {
    return <ReactIcon icon={avatar.icon} />;
  }
  return getInitials(name);
};

export const ActivityItemContent: React.FC<ActivityItemContentProps> = ({
  item,
  selectedTeam,
  homeProjectDetails,
}) => {
  const itemWithAction = { ...item, action: item.eventType };
  const { label, description } = useActivityItem(itemWithAction, selectedTeam);

  const project = homeProjectDetails.find((p) => p._id === item.project);
  const workspaceProject = selectedTeam?.projects?.find(
    (p) => p._id === item.project
  );
  const projectName = project?.name || workspaceProject?.projectName || "";
  const avatar = (project?.avatar || workspaceProject?.avatar) as
    | IAvatar
    | undefined;

  return (
    <div className="flex w-full flex-col gap-1 rounded-md border border-gray-100 px-3.5 py-3">
      <div className="flex items-center gap-2">
        <LetterCard
          className={`!size-5 !min-w-5 !min-h-5 rounded`}
          backgroundColor={avatar?.color || "#3B82F6"}
          letterClassName="text-secondary text-[10px] font-bold"
          letter={renderLetter(avatar, projectName)}
        />
        {projectName === "Unknown Project" ? (
          <Skeleton className="h-3 w-24 rounded" />
        ) : (
          <span className="text-xs text-secondary">{projectName}</span>
        )}
      </div>

      <div className="flex w-full items-center justify-between gap-2">
        <div className="truncate">
          <span className="text-[10px] font-medium leading-[18px] text-[#6B7280]">
            {label}:{" "}
          </span>
          <span className="text-xs font-normal leading-[18px] text-[#374151]">
            <HighlightQuotedText text={description} />
          </span>
        </div>
        <span className="shrink-0 whitespace-nowrap text-xs text-[#9CA3AF]">
          {item.timestamp ? dateFormatter(item.timestamp, "auditLog") : ""}
        </span>
      </div>
    </div>
  );
};

export default React.memo(ActivityItemContent);
